import Head from 'next/head';
import React, { useEffect, useRef } from 'react';
import sendGaComponentEvent from '@gcloud-npm/utils.metrics.send-ga-component-event';

const MOVISTAR_LOGO_URL =
  'https://prod.adb.col.movistar.es/static/icons/m-botando.svg';
const REDIRECT_DELAY_MS = 300;
const WAIT_FOR_GTAG_MS = 500;

const REDIRECT_TARGETS = {
  android: {
    destinationName: 'google-play',
    url: 'https://play.google.com/store/apps/details?id=com.movistar.android.mimovistar.es',
  },
  ios: {
    destinationName: 'app-store',
    url: 'https://apps.apple.com/es/app/mi-movistar/id1246644017',
  },
  other: {
    destinationName: 'web',
    url: 'https://www.movistar.es/atencion-cliente/app-mi-movistar',
  },
};

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const waitForGtag = async () => {
  const initialTime = Date.now();

  while (
    typeof window.gtag !== 'function' &&
    Date.now() - initialTime < WAIT_FOR_GTAG_MS
  ) {
    await wait(50);
  }
};

const appendQueryParams = (url) => {
  const currentParams = window.location.search;

  if (!currentParams) {
    return url;
  }

  if (url.indexOf('?') !== -1) {
    return `${url}&${currentParams.substring(1)}`;
  }

  return `${url}${currentParams}`;
};

const getOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
  const platform = navigator.platform || '';
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const isAndroid = /android/i.test(userAgent);
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === 'MacIntel' && maxTouchPoints > 1);

  if (isAndroid) {
    return 'android';
  }

  if (isIOS && !window.MSStream) {
    return 'ios';
  }

  return 'other';
};

const getRedirectTarget = () => {
  const operatingSystem = getOperatingSystem();

  return {
    operatingSystem,
    ...REDIRECT_TARGETS[operatingSystem],
  };
};

const sendRedirectEvent = async ({
  destinationName,
  destinationUrl,
  operatingSystem,
}) => {
  try {
    await waitForGtag();

    sendGaComponentEvent({
      componentName: 'app-markets',
      componentVariation: operatingSystem,
      componentContent: destinationName,
      componentProduct: 'mi-movistar',
      destinationName,
      destinationUrl,
      operatingSystem,
      eventType: 'redirect',
    });
  } catch (error) {
    console.error('Error sending app markets redirect event', error);
  }
};

const AppMarketsRedirect = () => {
  const redirectStarted = useRef(false);

  useEffect(() => {
    if (redirectStarted.current) {
      return undefined;
    }

    redirectStarted.current = true;

    let cancelled = false;
    let redirectTimeoutId;

    const redirect = async () => {
      const target = getRedirectTarget();
      const destinationUrl = appendQueryParams(target.url);

      await sendRedirectEvent({
        destinationName: target.destinationName,
        destinationUrl,
        operatingSystem: target.operatingSystem,
      });

      redirectTimeoutId = window.setTimeout(() => {
        if (!cancelled) {
          window.location.replace(destinationUrl);
        }
      }, REDIRECT_DELAY_MS);
    };

    redirect();

    return () => {
      cancelled = true;
      window.clearTimeout(redirectTimeoutId);
    };
  }, []);

  return (
    <>
      <Head>
        <title>Redirección Mi Movistar</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>
      <div className="layer">
        <img
          className="logo"
          alt="Logo Movistar"
          src={MOVISTAR_LOGO_URL}
          width="130"
          height="130"
        />
      </div>
      <style jsx>{`
        :global(body) {
          margin: 0;
        }

        :global(#cookies-bar-component) {
          opacity: 0;
        }

        .layer {
          align-items: center;
          display: flex;
          flex-direction: column;
          height: 100vh;
          justify-content: center;
          width: 100%;
        }

        .logo {
          max-width: 130px;
          min-width: 100px;
          width: 15%;
        }
      `}</style>
    </>
  );
};

export default AppMarketsRedirect;
